/**
 * Konvertiert eine Bounding Box von Pixel-Koordinaten zu YOLO-Format
 * YOLO-Format: class_id center_x center_y width height (alle Werte normalisiert 0-1)
 * 
 * @param {Object} bbox - Bounding Box im Format {x, y, width, height} in Pixeln
 * @param {number} imageWidth - Breite des Bildes in Pixeln
 * @param {number} imageHeight - Höhe des Bildes in Pixeln
 * @param {number} classId - Klassen-ID (0-indexiert)
 * @returns {string} YOLO-Format String: "class_id center_x center_y width height"
 */
export const normalizeToYOLO = (bbox, imageWidth, imageHeight, classId) => {
    if (!bbox || imageWidth <= 0 || imageHeight <= 0) return null;

    // Berechne Center-Punkt
    const centerX = bbox.x + (bbox.width / 2);
    const centerY = bbox.y + (bbox.height / 2);

    // Normalisiere auf 0-1
    const normalizedCenterX = centerX / imageWidth;
    const normalizedCenterY = centerY / imageHeight;
    const normalizedWidth = bbox.width / imageWidth;
    const normalizedHeight = bbox.height / imageHeight;

    // Stelle sicher, dass alle Werte im Bereich 0-1 sind
    const clampedCenterX = Math.max(0, Math.min(1, normalizedCenterX));
    const clampedCenterY = Math.max(0, Math.min(1, normalizedCenterY));
    const clampedWidth = Math.max(0, Math.min(1, normalizedWidth));
    const clampedHeight = Math.max(0, Math.min(1, normalizedHeight));

    // YOLO-Format: class_id center_x center_y width height
    return `${classId} ${clampedCenterX.toFixed(6)} ${clampedCenterY.toFixed(6)} ${clampedWidth.toFixed(6)} ${clampedHeight.toFixed(6)}`;
};

/**
 * Generiert eine YOLO-Label-Datei für mehrere Objekte
 * @param {Array<Object>} boundingBoxes - Array von Bounding Boxes mit classId
 * @param {number} imageWidth - Breite des Bildes
 * @param {number} imageHeight - Höhe des Bildes
 * @returns {string} YOLO-Label-String (mehrere Zeilen, eine pro Objekt)
 */
export const generateYOLOLabel = (boundingBoxes, imageWidth, imageHeight) => {
    if (!boundingBoxes || boundingBoxes.length === 0) return '';

    const labelLines = boundingBoxes
        .filter(bbox => bbox.classId !== undefined && bbox.classId !== null)
        .map(bbox => {
            const yoloLine = normalizeToYOLO(
                { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height },
                imageWidth,
                imageHeight,
                bbox.classId
            );
            return yoloLine;
        })
        .filter(line => line !== null);

    return labelLines.join('\n');
};

/**
 * Speichert eine YOLO-Label-Datei
 * @param {string} labelContent - Der Label-String
 * @param {string} filename - Dateiname (ohne Extension)
 * @returns {Blob} Blob-Objekt für die Label-Datei
 */
export const createYOLOLabelBlob = (labelContent, filename) => {
    const blob = new Blob([labelContent], { type: 'text/plain' });
    return blob;
};

